import type { TFunction } from "i18next";

export const WABA_ERROR_CODES = {
  // Media errors
  MEDIA_DOWNLOAD_ERROR: 131049,
  MEDIA_FILE_SIZE_TOO_BIG: 131052,
  MEDIA_TYPE_NOT_SUPPORTED: 131053,
  MEDIA_DOWNLOAD_FAILED: 131057,

  // Rate limiting
  RATE_LIMIT_HIT: 131045,
  MESSAGE_LIMIT_EXCEEDED: 131048,
  OUTSIDE_24H_WINDOW: 131047,
  BUSINESS_RESTRICTED: 131056,

  // Template errors
  TEMPLATE_PARAM_COUNT_MISMATCH: 132000,
  TEMPLATE_NOT_FOUND: 132001,
  TEMPLATE_CONTENT_MISMATCH: 132002,
  TEMPLATE_PAUSED: 132005,
  TEMPLATE_DISABLED: 132007,
  TEMPLATE_PARAMS_INVALID: 132012,
  TEMPLATE_QUALITY_LOW: 132015,

  // User errors
  INVALID_RECIPIENT: 131026,
  RECIPIENT_NOT_IN_ALLOWED_LIST: 131030,
  USER_NOT_OPTED_IN: 131042,
  RECIPIENT_BLOCKED: 131021,

  // System errors
  CLOUD_API_RATE_LIMIT: 130429,
  INTERNAL_ERROR: 131000,
  ACCESS_DENIED: 131005,
  ACCOUNT_LOCKED: 131031,

  // Message errors
  MESSAGE_TOO_OLD: 131014,
  MESSAGE_TYPE_NOT_SUPPORTED: 131016,
  UNSUPPORTED_MESSAGE: 131051,

  // API parameter errors
  REQUIRED_PARAMETER_MISSING: 131008,
  PARAMETER_VALUE_INVALID: 131009,
  PHONE_NUMBER_NOT_REGISTERED: 133010,
  PHONE_NUMBER_REVERIFICATION_NEEDED: 133015,

  DISPLAY_NAME_APPROVAL_REQUIRED: 131037,
  TEMPORARILY_UNAVAILABLE: 368,
  USER_EXPERIMENT: 130472,
} as const;

export const WABA_ERROR_TRANSLATION_KEYS: Record<number, string> = {
  // Media errors
  [WABA_ERROR_CODES.MEDIA_DOWNLOAD_ERROR]: "waba_errors.media_download_error",
  [WABA_ERROR_CODES.MEDIA_FILE_SIZE_TOO_BIG]: "waba_errors.media_file_size_too_big",
  [WABA_ERROR_CODES.MEDIA_TYPE_NOT_SUPPORTED]: "waba_errors.media_type_not_supported",
  [WABA_ERROR_CODES.MEDIA_DOWNLOAD_FAILED]: "waba_errors.media_download_failed",

  // Rate limiting
  [WABA_ERROR_CODES.RATE_LIMIT_HIT]: "waba_errors.rate_limit_hit",
  [WABA_ERROR_CODES.MESSAGE_LIMIT_EXCEEDED]: "waba_errors.message_limit_exceeded",
  [WABA_ERROR_CODES.OUTSIDE_24H_WINDOW]: "waba_errors.outside_24h_window",
  [WABA_ERROR_CODES.BUSINESS_RESTRICTED]: "waba_errors.business_restricted",

  // Template errors
  [WABA_ERROR_CODES.TEMPLATE_PARAM_COUNT_MISMATCH]: "waba_errors.template_param_count_mismatch",
  [WABA_ERROR_CODES.TEMPLATE_NOT_FOUND]: "waba_errors.template_not_found",
  [WABA_ERROR_CODES.TEMPLATE_CONTENT_MISMATCH]: "waba_errors.template_content_mismatch",
  [WABA_ERROR_CODES.TEMPLATE_PAUSED]: "waba_errors.template_paused",
  [WABA_ERROR_CODES.TEMPLATE_DISABLED]: "waba_errors.template_disabled",
  [WABA_ERROR_CODES.TEMPLATE_PARAMS_INVALID]: "waba_errors.template_params_invalid",
  [WABA_ERROR_CODES.TEMPLATE_QUALITY_LOW]: "waba_errors.template_quality_low",

  // User errors
  [WABA_ERROR_CODES.INVALID_RECIPIENT]: "waba_errors.invalid_recipient",
  [WABA_ERROR_CODES.RECIPIENT_NOT_IN_ALLOWED_LIST]: "waba_errors.recipient_not_in_allowed_list",
  [WABA_ERROR_CODES.USER_NOT_OPTED_IN]: "waba_errors.user_not_opted_in",
  [WABA_ERROR_CODES.RECIPIENT_BLOCKED]: "waba_errors.recipient_blocked",

  // System errors
  [WABA_ERROR_CODES.CLOUD_API_RATE_LIMIT]: "waba_errors.cloud_api_rate_limit",
  [WABA_ERROR_CODES.INTERNAL_ERROR]: "waba_errors.internal_error",
  [WABA_ERROR_CODES.ACCESS_DENIED]: "waba_errors.access_denied",
  [WABA_ERROR_CODES.ACCOUNT_LOCKED]: "waba_errors.account_locked",

  // Message errors
  [WABA_ERROR_CODES.MESSAGE_TOO_OLD]: "waba_errors.message_too_old",
  [WABA_ERROR_CODES.MESSAGE_TYPE_NOT_SUPPORTED]: "waba_errors.message_type_not_supported",
  [WABA_ERROR_CODES.UNSUPPORTED_MESSAGE]: "waba_errors.unsupported_message",

  // API parameter errors
  [WABA_ERROR_CODES.REQUIRED_PARAMETER_MISSING]: "waba_errors.required_parameter_missing",
  [WABA_ERROR_CODES.PARAMETER_VALUE_INVALID]: "waba_errors.parameter_value_invalid",
  [WABA_ERROR_CODES.PHONE_NUMBER_NOT_REGISTERED]: "waba_errors.phone_number_not_registered",
  [WABA_ERROR_CODES.PHONE_NUMBER_REVERIFICATION_NEEDED]: "waba_errors.phone_number_reverification_needed",

  [WABA_ERROR_CODES.DISPLAY_NAME_APPROVAL_REQUIRED]: "waba_errors.display_name_approval_required",
  [WABA_ERROR_CODES.TEMPORARILY_UNAVAILABLE]: "waba_errors.temporarily_unavailable",
  [WABA_ERROR_CODES.USER_EXPERIMENT]: "waba_errors.user_experiment",
};

export function getWabaErrorMessage(t: TFunction, errorCode: number): string {
  const translationKey = WABA_ERROR_TRANSLATION_KEYS[errorCode];
  if (translationKey) {
    return t(translationKey);
  }

  return t("waba_errors.generic_error", { errorCode });
}
